import {
  buildGuide,
  paragraphs,
  bullets,
  steps,
  callout,
  workflow,
  faq,
  table,
  fieldTable,
  permissionTable,
  heading,
} from '../helpers';

const custPerm = permissionTable([
  [
    'manage_customer_management',
    'Required for Customer Hub dashboard, list, profiles, documents, and retention',
  ],
]);

export const customerGuides = [
  buildGuide({
    id: 'customer-overview',
    title: 'Customer Hub Overview',
    moduleId: 'customer',
    summary:
      'Guest profiles, history, documents, and retention campaigns in one place.',
    keywords: [
      'customer hub',
      'guests',
      'crm',
      'guest profile',
      'repeat guest',
    ],
    pathHint: 'Hotel → Customer Hub',
    related: [
      'customer-dashboard',
      'customer-list',
      'customer-detail',
      'ai-retention',
      'add-customer',
    ],
    overview: [
      ...paragraphs(
        'Customer Hub stores guest identity beyond a single booking. Use it to recognise repeat guests, view stay history, keep ID documents, capture preference notes, and run retention offers through configured communication channels.',
        'Front office still captures guest fields on reservations and walk-ins. Customer Hub deepens CRM over time so the second and tenth stay feel personal — and so compliance documents are easier to find.',
        'The practical golden key is the mobile number. Consistent mobiles prevent duplicate profiles, power search, and enable WhatsApp offers when Communications is configured.',
        'AI Retention helps identify lapsed guests by behaviour segments and send win-back messages. It is an accelerator, not a replacement for warm FO recognition at the desk.',
        'Customer Hub is staff-facing. NexaStay guest self-profile (for direct bookers) is separate. Do not promise guests they can edit Hub records themselves from the public site.'
      ),
      table(
        ['Area', 'Use it for'],
        [
          ['Dashboard', 'Growth, demographics, top repeaters'],
          ['Customer List', 'Search directory; avoid duplicates'],
          ['Add Customer', 'Create profile before/without a booking'],
          ['Customer Detail', '360° profile, history, documents, notes, offers'],
          ['AI Retention', 'Segmented win-back / offer campaigns'],
        ]
      ),
    ],
    beforeYouBegin: [
      custPerm,
      bullets([
        'Agree that staff must search by mobile before adding a guest.',
        'Configure Communications (email/WhatsApp) before sending offers.',
        'Train what may never be stored in notes (full card PANs, passwords).',
      ]),
    ],
    steps: [
      heading('Day-to-day CRM habit'),
      steps([
        {
          text: 'Review Customer Dashboard weekly for repeater and growth signals.',
        },
        {
          text: 'Before creating anyone, search Customer List by mobile (and name).',
        },
        {
          text: 'Open Customer Detail to confirm contacts, documents, and preference notes.',
        },
        {
          text: 'Link or create bookings from the correct profile so history stays clean.',
        },
        {
          text: 'Use AI Retention for approved win-back campaigns when Communications is ready.',
        },
      ]),
    ],
    screens: [
      bullets(
        [
          'Customer Dashboard — KPIs and charts',
          'Customer List — searchable directory + Add',
          'Add Customer drawer/page — profile + optional documents',
          'Customer Detail — history, documents, notes, VIP cues, offers',
          'AI Retention — segments, previews, send',
        ],
        'Main Customer Hub screens'
      ),
    ],
    workflow: [
      workflow('Repeat guest arrival', [
        'Search by mobile',
        'Open profile',
        'Read notes & documents',
        'Create/link booking',
        'Personalise stay',
      ]),
    ],
    businessLogic: [
      ...paragraphs(
        'Bookings can link to customer records so history accumulates. Duplicate mobiles are blocked/warned on add — use the existing profile instead of forcing a second record.',
        'Retention tools score/segment guests and send via email/WhatsApp only when Customer offer events are enabled in Communications settings. FO must honour offers at booking time.'
      ),
    ],
    tips: [
      callout(
        'tip',
        'Mobile is the key',
        'Consistent mobile numbers prevent duplicate profiles and unlock WhatsApp offers.'
      ),
      callout(
        'tip',
        'Notes that matter',
        'Write actionable preferences (“high floor”, “extra towels”, “no feather pillows”) — not vague “VIP” with no detail.'
      ),
      callout(
        'warning',
        'Never store card PANs',
        'Notes and documents are not a place for full payment card numbers.'
      ),
    ],
    bestPractices: [
      bullets([
        'Search before every add — duplicates poison CRM and retention.',
        'Keep documents current for faster re-check-in and compliance.',
        'Update mobile immediately when a guest changes number.',
        'Brief FO on active retention offers before campaigns send.',
        'Use VIP cues from stay counts to prioritise recognition, not only discounts.',
        'Merge duplicates only via an agreed supervisor SOP.',
      ]),
    ],
    commonMistakes: [
      bullets([
        'Creating a new guest every stay without searching.',
        'Wrong country code on mobile numbers.',
        'Storing sensitive payment data in notes.',
        'Sending retention offers the desk cannot honour.',
        'Ignoring document quality until police log day.',
        'Mixing suppliers into Customer Hub (vendors belong under Stock → Suppliers).',
      ]),
    ],
    troubleshooting: [
      table(
        ['Problem', 'Likely cause', 'What to do'],
        [
          [
            'Duplicate guests',
            'Added without search / changed mobile',
            'Search by mobile; use existing profile; follow merge SOP',
          ],
          [
            'Hub menu missing',
            'Permission/package',
            'Ask admin for manage_customer_management',
          ],
          [
            'Offers fail to send',
            'Communications not configured',
            'Enable customer_offer email/WhatsApp in Configuration',
          ],
          [
            'History empty',
            'Bookings not linked to profile',
            'Link future stays; avoid creating parallel profiles',
          ],
        ]
      ),
    ],
    faqs: [
      faq([
        {
          q: 'Is this required for walk-ins?',
          a: 'Walk-in captures guest fields on the booking; Customer Hub deepens CRM and documents over time. Search/link whenever the guest is known.',
        },
        {
          q: 'Same as Booking List?',
          a: 'Booking List is stays. Customer Hub is the person across stays.',
        },
        {
          q: 'Can guests edit Hub profiles?',
          a: 'Staff-facing Customer Hub is internal. NexaStay guest profile is separate for direct bookers.',
        },
        {
          q: 'Where do vendors go?',
          a: 'Stock → Suppliers — not Customer Hub.',
        },
      ]),
    ],
  }),

  buildGuide({
    id: 'customer-dashboard',
    title: 'Customer Dashboard',
    moduleId: 'customer',
    summary: 'Guest growth, demographics, and top repeaters at a glance.',
    keywords: [
      'customer dashboard',
      'guest kpi',
      'repeaters',
      'crm dashboard',
    ],
    pathHint: 'Customer Hub → Dashboard',
    related: ['customer-list', 'ai-retention', 'reports-customers'],
    overview: [
      ...paragraphs(
        'Customer Dashboard visualises guest-base growth, demographic/location patterns, and top repeat guests so managers can plan recognition and campaigns with evidence.',
        'Use it before festival campaigns and monthly CRM reviews. Top repeaters are the guests FO should greet by name — dashboard tells you who.',
        'Charts need history volume. A brand-new property with few profiles will look empty; that is expected until stays accumulate.',
        'For raw exports, use Reports → Customers. The dashboard is insight and prioritisation, not the compliance register.'
      ),
    ],
    beforeYouBegin: [
      custPerm,
      bullets([
        'Pick a date range that matches the campaign or review window.',
        'Have Customer List ready to open specific guests.',
      ]),
    ],
    steps: [
      steps([
        { text: 'Open Customer Hub → Dashboard.' },
        {
          text: 'Set the date range for growth and activity context.',
        },
        {
          text: 'Review KPI cards (guest counts, new guests, loyalty-oriented metrics as shown).',
        },
        {
          text: 'Scan demographic/location charts and top repeater tables.',
        },
        {
          text: 'Jump to Customer List or AI Retention to act on what you learned.',
        },
        {
          text: 'Download PDF when preparing an owner CRM pack if available on the page.',
        },
      ]),
    ],
    screens: [
      bullets([
        'Date range header',
        'KPI metric cards',
        'Growth / demographic charts',
        'Top repeaters table',
        'PDF download where provided',
      ]),
      table(
        ['Insight', 'Action'],
        [
          ['Rising new guests', 'Ensure profiles are clean; capture email/mobile'],
          ['Strong repeater list', 'Personal greetings; consider retention offers'],
          ['Thin data', 'Focus on FO capture quality before big campaigns'],
        ]
      ),
    ],
    workflow: [
      workflow('Monthly CRM review', [
        'Open dashboard',
        'Identify VIPs / repeaters',
        'Plan offers or gestures',
        'Brief FO',
      ]),
    ],
    businessLogic: [
      ...paragraphs(
        'Aggregates customer and stay history metrics for the selected range. VIP-style cues elsewhere in Hub often derive from booking counts on the profile.',
        'Empty charts usually mean insufficient guest/booking history — not a broken dashboard.'
      ),
    ],
    tips: [
      callout(
        'tip',
        'VIPs',
        'Personally greet top repeaters — the dashboard names them; the desk delivers the experience.'
      ),
      callout(
        'tip',
        'Before festivals',
        'Review repeaters two weeks out and plan rooms/amenities — not only discounts.'
      ),
      callout(
        'note',
        'Export path',
        'Need a spreadsheet? Use Reports → Customers rather than screenshotting charts.'
      ),
    ],
    bestPractices: [
      bullets([
        'Review before festival and long-weekend campaigns.',
        'Share top repeater names with FO in the morning briefing.',
        'Pair dashboard insights with AI Retention only after offer rules are approved.',
        'Track whether new-guest growth is real or duplicate-driven.',
        'Revisit demographics seasonally (wedding vs leisure mix).',
      ]),
    ],
    commonMistakes: [
      bullets([
        'Reading charts without noticing the date range.',
        'Launching blasts from vanity metrics without FO briefing.',
        'Ignoring data quality when charts look “too good”.',
        'Treating dashboard as a police/compliance export.',
        'Never opening profiles behind the repeater names.',
      ]),
    ],
    troubleshooting: [
      table(
        ['Problem', 'Likely cause', 'What to do'],
        [
          [
            'Empty charts',
            'Low history volume',
            'Keep capturing clean profiles; widen range',
          ],
          [
            'Odd spikes',
            'Bulk imports / duplicates',
            'Audit Customer List for duplicate mobiles',
          ],
          [
            'PDF issues',
            'Still loading / browser block',
            'Wait; allow downloads; retry',
          ],
          [
            'Cannot open',
            'Permission',
            'Ask admin for manage_customer_management',
          ],
        ]
      ),
    ],
    faqs: [
      faq([
        {
          q: 'Can I export?',
          a: 'Use Customers report in Reports Hub for registry exports; dashboard may offer PDF snapshots.',
        },
        {
          q: 'How is a repeater defined?',
          a: 'From stay/booking history linked to the customer — open the profile for exact counts.',
        },
        {
          q: 'Useful for small boutique hotels?',
          a: 'Yes — even a short top-repeater list improves recognition.',
        },
        {
          q: 'Replace AI Retention?',
          a: 'No — dashboard informs; Retention executes segmented outreach.',
        },
      ]),
    ],
  }),

  buildGuide({
    id: 'customer-list',
    title: 'Customer List',
    moduleId: 'customer',
    summary: 'Searchable guest directory for the property.',
    keywords: [
      'customer list',
      'guest directory',
      'search guest',
      'find guest',
    ],
    pathHint: 'Customer Hub → List',
    related: ['add-customer', 'customer-detail', 'reports-customers'],
    overview: [
      ...paragraphs(
        'Customer List is the property guest directory. Search here before adding anyone — duplicate prevention starts on this screen.',
        'Search by name, mobile, or email. When the guest is unsure of the full number, try the last digits and confirm identity with ID or past stay details.',
        'From the list you open Customer Detail or launch Add Customer. Treat Add as a last step after a failed careful search.',
        'Visibility depends on how customers are scoped for your deployment. Always confirm you are in the correct hotel context before concluding a guest “does not exist”.'
      ),
    ],
    beforeYouBegin: [
      custPerm,
      bullets([
        'Ask the guest for mobile with country code.',
        'Have alternate spellings ready for uncommon names.',
      ]),
    ],
    steps: [
      steps([
        { text: 'Open Customer Hub → List.' },
        {
          text: 'Search by mobile first (most reliable), then name/email.',
        },
        {
          text: 'Open the matching guest to verify identity (name, ID, past stays).',
        },
        {
          text: 'If no match after thorough search, click Add Customer.',
        },
        {
          text: 'Reset filters if the list looks wrongly empty.',
        },
      ]),
    ],
    screens: [
      bullets([
        'Search and filter controls',
        'Sortable guest table',
        'Pagination',
        'Add Customer action / drawer',
        'Row open → Customer Detail',
      ]),
      fieldTable([
        ['Search (name/mobile/email)', 'Recommended', 'Find existing profiles'],
        ['Filters / sort', 'Optional', 'Narrow large directories'],
        ['Add Customer', 'When new', 'Creates a profile after search fails'],
      ]),
    ],
    workflow: [
      workflow('Find guest', [
        'Search mobile',
        'Confirm identity',
        'Open detail',
        'Book or update',
      ]),
    ],
    businessLogic: [
      ...paragraphs(
        'List shows customer master rows for the hotel/org context of your session.',
        'Add flows check for existing mobiles and block/warn on duplicates — honour that warning.'
      ),
    ],
    tips: [
      callout(
        'tip',
        'Partial mobile',
        'Try last 4–6 digits if the full number is uncertain, then confirm with the guest.'
      ),
      callout(
        'tip',
        'Name variants',
        'Search both given-name-first and family-name-first styles for international guests.'
      ),
      callout(
        'note',
        'Corporate bookers',
        'Ask for the traveller mobile, not only the company landline.'
      ),
    ],
    bestPractices: [
      bullets([
        'Standardise name casing for readability.',
        'Ask alternate numbers for corporate travellers when useful.',
        'Teach every new joiner: search → then add.',
        'Clear filters before declaring “not found”.',
        'Open detail to verify — similar names are common.',
      ]),
    ],
    commonMistakes: [
      bullets([
        'Adding without search.',
        'Searching only by first name in a large database.',
        'Ignoring duplicate mobile warnings.',
        'Working in the wrong hotel context.',
        'Creating a second profile because email differed but mobile matched.',
      ]),
    ],
    troubleshooting: [
      table(
        ['Problem', 'Likely cause', 'What to do'],
        [
          [
            'Not found',
            'Spelling / wrong hotel / never created',
            'Retry search; check Booking List; then add',
          ],
          [
            'Too many similar names',
            'Common names',
            'Use mobile; verify ID on detail',
          ],
          [
            'List empty',
            'Filters stuck / new property',
            'Reset filters; confirm permissions',
          ],
          [
            'Cannot add',
            'Permission',
            'Ask admin for customer management access',
          ],
        ]
      ),
    ],
    faqs: [
      faq([
        {
          q: 'Org-wide guests?',
          a: 'Visibility depends on how customers are scoped for your deployment; always confirm hotel context.',
        },
        {
          q: 'Can I export from here?',
          a: 'Use Reports → Customers for registry exports.',
        },
        {
          q: 'Guest exists on a booking but not in list?',
          a: 'Booking-embedded fields may not yet be a full customer row — add/link a profile when CRM matters.',
        },
        {
          q: 'Sort by last stay?',
          a: 'Use available sort controls; otherwise open detail history for exact stays.',
        },
      ]),
    ],
  }),

  buildGuide({
    id: 'add-customer',
    title: 'Add Customer',
    moduleId: 'customer',
    summary:
      'Create a guest profile manually for CRM and future bookings.',
    keywords: [
      'add customer',
      'create guest',
      'new guest',
      'guest profile create',
    ],
    pathHint: 'Customer Hub → Add',
    related: ['customer-list', 'customer-detail', 'walk-in'],
    overview: [
      ...paragraphs(
        'Add Customer creates a guest profile when you want CRM data before or without an immediate booking — for example pre-registering a VIP, capturing a corporate traveller early, or cleaning up directory gaps.',
        'Many guests are also created implicitly through booking and walk-in flows. That does not remove the duty to search first; implicit creation is how duplicates multiply.',
        'The form captures full name (parsed into first/middle/last), mobile with country code, email, gender, address/location, date of birth, alternate mobile, and optional ID document type with front/back uploads or camera capture.',
        'Mobile uniqueness is enforced: if a customer with the same mobile already exists, fix your search and open the existing profile instead of forcing a duplicate.',
        'Documents added here help future check-ins and compliance, but arrival-time verification on the booking may still be required by your property SOP and local rules.'
      ),
    ],
    beforeYouBegin: [
      custPerm,
      bullets([
        'Completed a thorough Customer List search.',
        'Guest mobile with correct country code ready.',
        'ID document available if you plan to upload now.',
      ]),
    ],
    steps: [
      heading('Create a profile'),
      steps([
        {
          text: 'From Customer List, confirm search found nobody — then open Add Customer.',
        },
        {
          text: 'Enter full name carefully (spelling as on ID when possible).',
        },
        {
          text: 'Enter mobile with the correct country/dial code; add alternate mobile if provided.',
        },
        {
          text: 'Add email, gender, DOB, and address/city/state/country fields as available.',
        },
        {
          text: 'Optional: select document type and upload/capture front (and back if required).',
        },
        {
          text: 'Save. If a duplicate mobile warning appears, open the existing profile instead.',
        },
        {
          text: 'Open Customer Detail to add preference notes and verify documents.',
        },
      ]),
    ],
    screens: [
      fieldTable([
        ['Full name', 'Yes', 'Parsed into first/middle/last for the profile'],
        ['Mobile + dial code', 'Yes', 'Primary search key; uniqueness checked'],
        ['Email', 'No', 'Receipts and email offers'],
        ['Gender / DOB', 'Optional', 'Profile completeness / personalisation'],
        ['Address / City / State / Country', 'Optional', 'CRM and demographic insight'],
        ['Alternate mobile', 'Optional', 'Backup contact'],
        ['Document type + images', 'Optional here', 'ID front/back via upload or camera'],
      ]),
      bullets([
        'Add Customer drawer/page form',
        'Camera capture for documents',
        'Validation messages for phone/duplicates',
      ]),
    ],
    workflow: [
      workflow('Pre-register VIP', [
        'Search list',
        'Add customer',
        'Upload ID if available',
        'Note preferences',
        'Create reservation linked',
      ]),
    ],
    businessLogic: [
      ...paragraphs(
        'Saved profiles are reusable across stays. Duplicate mobile checks protect the directory.',
        'Document uploads store images for staff use; ID number validation rules may apply by document type on detail/edit flows.'
      ),
    ],
    tips: [
      callout(
        'note',
        'From booking',
        'Many guests are created via booking flows — still search first to avoid parallel profiles.'
      ),
      callout(
        'tip',
        'Consent',
        'If you will send WhatsApp/email offers, capture consent per property policy while adding the guest.'
      ),
      callout(
        'warning',
        'Country code',
        'Wrong dial codes create “new” mobiles that never match the guest’s real WhatsApp.'
      ),
    ],
    bestPractices: [
      bullets([
        'Capture consent for WhatsApp/email offers when relevant.',
        'Match name spelling to the ID document.',
        'Upload clear document photos — glare causes compliance failures.',
        'Add preference notes immediately after save for VIPs.',
        'Use alternate mobile for corporates who book via assistants.',
        'Never invent emails like noreply@test.com just to skip a field.',
      ]),
    ],
    commonMistakes: [
      bullets([
        'Wrong country code on mobile.',
        'Skipping search and creating duplicates.',
        'Blurry ID photos that fail later verification.',
        'Putting company name in the guest name field without the traveller name.',
        'Ignoring duplicate mobile errors and retrying with tiny edits.',
        'Storing card numbers in notes after save.',
      ]),
    ],
    troubleshooting: [
      table(
        ['Problem', 'Likely cause', 'What to do'],
        [
          [
            'Duplicate warning',
            'Mobile already exists',
            'Open existing profile; do not create another',
          ],
          [
            'Phone validation error',
            'Dial code / length',
            'Fix country code and national number',
          ],
          [
            'Camera blocked',
            'Browser permission',
            'Allow camera or upload a file instead',
          ],
          [
            'Save failed',
            'Required fields / network',
            'Check required inputs; retry',
          ],
        ]
      ),
    ],
    faqs: [
      faq([
        {
          q: 'Required documents?',
          a: 'You can add documents on add/detail; booking-time ID verification may still be required for stays and police log quality.',
        },
        {
          q: 'Must email be unique?',
          a: 'Mobile is the practical uniqueness key. Email helps offers and receipts but may be shared in families — confirm identity carefully.',
        },
        {
          q: 'Can I add without mobile?',
          a: 'Mobile is effectively required for searchable CRM. Avoid incomplete profiles when possible.',
        },
        {
          q: 'Difference from walk-in?',
          a: 'Walk-in creates a stay now. Add Customer creates/updates the person record, with or without an immediate booking.',
        },
      ]),
    ],
  }),

  buildGuide({
    id: 'customer-detail',
    title: 'Customer Detail (Profile, History, Documents)',
    moduleId: 'customer',
    summary:
      'View profile, past bookings, documents, notes, and offers for one guest.',
    keywords: [
      'guest profile',
      'guest history',
      'guest documents',
      'guest notes',
      'vip',
      'customer offer',
    ],
    pathHint: 'Customer → Detail',
    related: ['customer-list', 'booking-list', 'ai-retention', 'reports-police'],
    overview: [
      ...paragraphs(
        'Customer Detail is the guest 360° view: identity and contacts, booking history, ID documents (with side editors and camera/upload), preference notes, VIP-style cues from stay counts, and one-to-one offer messaging when Communications allows.',
        'Open this screen before special arrivals. History tells you rate sensitivity and room preferences; notes tell housekeepers what “good service” means for this person.',
        'Documents support faster re-check-in and compliance. Keep them readable and current; expire or replace old scans per property policy.',
        'Offer dialogs can send email and/or WhatsApp when customer_offer channels are enabled. Only promise discounts and amenities you can honour on the dates offered.',
        'VIP tiers shown from booking counts (for example higher tiers at higher stay counts) are recognition aids — they do not automatically change rates unless your team applies an offer or rate plan.'
      ),
    ],
    beforeYouBegin: [
      custPerm,
      bullets([
        'Have the guest’s latest mobile/email if updating contacts.',
        'Know whether Communications is enabled before sending offers.',
      ]),
    ],
    steps: [
      steps([
        { text: 'Open a customer from Customer List (or a booking link into the profile).' },
        {
          text: 'Verify name, mobile, email, and address; edit profile fields if needed.',
        },
        {
          text: 'Review booking history for preferences, sources, and payment patterns.',
        },
        {
          text: 'Upload/update document front/back; set document type and ID number carefully.',
        },
        {
          text: 'Add or refresh notes (allergies, pillow, high floor, late checkout habits).',
        },
        {
          text: 'When appropriate, open the offer dialog, choose channels, and send a clear message.',
        },
        {
          text: 'Click into a past booking when you need folio-level investigation.',
        },
      ]),
    ],
    screens: [
      bullets([
        'Profile header with VIP-style cues',
        'Editable profile fields',
        'Booking history table with status/payment chips',
        'Document viewers/editors (front/back, camera, upload)',
        'Notes field',
        'Offer send dialog (email/WhatsApp)',
      ]),
      fieldTable([
        ['Contacts (mobile/email)', 'Critical', 'Search + communications'],
        ['Document type / ID number', 'Compliance', 'Validated by type rules where applicable'],
        ['Document images', 'Recommended', 'Front/back via upload or camera'],
        ['Notes', 'Recommended', 'Actionable preferences for FO/HK'],
        ['Offer message + channels', 'Optional', 'One-to-one outreach when enabled'],
      ]),
    ],
    workflow: [
      workflow('Personalise stay', [
        'Open profile',
        'Read notes & history',
        'Allocate preferred room',
        'Check-in with documents ready',
      ]),
    ],
    businessLogic: [
      ...paragraphs(
        'History links to bookings associated with the customer. Empty history usually means stays were never linked to this profile (duplicates).',
        'Document side updates save to the guest record. Aadhaar and other types may mask or validate numbers according to formatter rules in the UI.',
        'Offers send only on enabled channels; guests without email/phone cannot receive that channel.'
      ),
    ],
    tips: [
      callout(
        'tip',
        'Notes that matter',
        'Write actionable notes (“no elevator”, “extra towels”) not vague ones.'
      ),
      callout(
        'tip',
        'VIP cue',
        'Use stay-count VIP indicators to prioritise recognition — still verify the person at the desk.'
      ),
      callout(
        'warning',
        'Card data',
        'Never store payment card PANs in notes.'
      ),
      callout(
        'note',
        'Police quality',
        'Clear documents here make Reports → Police log much less painful.'
      ),
    ],
    bestPractices: [
      bullets([
        'Update mobile when the guest changes number.',
        'Replace old ID scans per retention policy.',
        'Read notes aloud in the FO-HK arrival briefing for VIPs.',
        'Honour offer texts exactly — copy the code/rate into the booking notes.',
        'Fix duplicate histories by stopping new duplicate creates.',
        'Verify document type before editing ID numbers.',
      ]),
    ],
    commonMistakes: [
      bullets([
        'Storing payment card PANs in notes.',
        'Sending offers without FO briefing.',
        'Leaving blurry passport images “for later”.',
        'Editing the wrong similarly named guest.',
        'Assuming VIP badge auto-applies a discount.',
        'Promising early check-in in notes without telling FO.',
      ]),
    ],
    troubleshooting: [
      table(
        ['Problem', 'Likely cause', 'What to do'],
        [
          [
            'History empty',
            'Bookings not linked / wrong profile',
            'Search duplicates; link future stays to this profile',
          ],
          [
            'Document save failed',
            'Network / validation',
            'Retry; check document type + ID format',
          ],
          [
            'Offer send failed',
            'Comms disabled / missing contact',
            'Enable customer_offer channels; add email/phone',
          ],
          [
            'ID validation error',
            'Wrong type or format',
            'Select correct document type; enter valid number',
          ],
        ]
      ),
    ],
    faqs: [
      faq([
        {
          q: 'Can guests edit this?',
          a: 'Staff-facing Customer Hub is internal. NexaStay guest profile is separate for direct bookers.',
        },
        {
          q: 'What do VIP labels mean?',
          a: 'They reflect stay-count tiers on the profile for recognition — apply rates/offers deliberately.',
        },
        {
          q: 'Are documents on the booking different?',
          a: 'Booking flows also capture IDs for the stay. Keep both consistent; Hub helps reuse for repeat visits.',
        },
        {
          q: 'Can I message from here and Retention?',
          a: 'Yes — detail offers are one-to-one; AI Retention is segmented campaigns. Coordinate so guests are not spammed.',
        },
      ]),
    ],
  }),

  buildGuide({
    id: 'ai-retention',
    title: 'AI Retention Campaigns',
    moduleId: 'customer',
    summary:
      'Run retention/offer campaigns to win back or reward guests.',
    keywords: [
      'ai retention',
      'campaign',
      'offers',
      'whatsapp offer',
      'win back',
      'lapsed guests',
    ],
    pathHint: 'Customer Hub → AI Retention',
    related: ['customer-dashboard', 'communications', 'customer-detail'],
    overview: [
      ...paragraphs(
        'AI Retention helps identify lapsed or priority guests by behaviour segments, personalise win-back messages, preview email/WhatsApp content, and send through configured communication channels.',
        'The flow typically scores guests, groups them into segments with offer hints, lets you edit per-segment message templates (with placeholders like guest name and hotel name), preview a sample, then send to selected guests.',
        'Email and WhatsApp only work when Configuration enables the customer_offer event on those channels. If send fails, fix Communications before rewriting copy.',
        'Campaigns are only as good as FO readiness. Do not send discounts you cannot honour on busy dates, and brief the desk on active offers before the first reply arrives.',
        'Classic relationship still matters. Retention is an accelerator for properties that already capture clean mobiles/emails and keep promises.'
      ),
    ],
    beforeYouBegin: [
      custPerm,
      bullets([
        'Communications: customer_offer enabled for email and/or WhatsApp.',
        'Offer rules approved by manager (dates, discount floors, blackout dates).',
        'FO briefed on how to apply the offer on reservations.',
      ]),
    ],
    steps: [
      heading('Run a win-back campaign'),
      steps([
        { text: 'Open Customer Hub → AI Retention.' },
        {
          text: 'Generate/identify the campaign guest set and review summary stats (identified, email-ready, WhatsApp-ready, scores).',
        },
        {
          text: 'Review behaviour segments and offer hints; filter to a segment if focusing the send.',
        },
        {
          text: 'Edit segment message templates; keep promises specific and honourable.',
        },
        {
          text: 'Preview email/WhatsApp for a sample guest; fix tone and placeholders.',
        },
        {
          text: 'Select guests/channels (email and/or WhatsApp as enabled) and send.',
        },
        {
          text: 'Track responses; when guests book, apply the same offer at the desk and note it on the booking.',
        },
      ]),
    ],
    screens: [
      bullets([
        'Campaign summary stat cards',
        'Behaviour segment list with counts/offer hints',
        'Guest selection table',
        'Segment message template editors',
        'Email / WhatsApp preview',
        'Channel toggles and send action',
      ]),
      fieldTable([
        ['Segment filter', 'Optional', 'Focus one behaviour group'],
        ['Message template', 'Yes', 'Per-segment copy with placeholders'],
        ['Channels', 'Yes', 'Email and/or WhatsApp if enabled'],
        ['Guest selection', 'Yes', 'Who receives this send'],
        ['Preview', 'Recommended', 'Verify merge fields before send'],
      ]),
    ],
    workflow: [
      workflow('Win-back', [
        'Identify lapsed guests',
        'Choose segment',
        'Craft honourable offer',
        'Preview',
        'Send',
        'Honour at desk',
      ]),
    ],
    businessLogic: [
      ...paragraphs(
        'Identification uses guest stay behaviour and scoring from the retention service. Guests without usable email/phone are skipped for that channel.',
        'Sends go through communication integrations. Templates can substitute fields such as guest name, hotel name, and offer hint text. FO must apply matching rates/codes when guests return.'
      ),
    ],
    tips: [
      callout(
        'warning',
        'Promise = policy',
        'Do not send discounts you cannot honour on busy dates or blackout periods.'
      ),
      callout(
        'tip',
        'Throttle',
        'Avoid weekly spam. Space campaigns so offers feel special.'
      ),
      callout(
        'tip',
        'Preview always',
        'Broken placeholders look unprofessional — preview before every send.'
      ),
      callout(
        'important',
        'FO briefing',
        'Print or post the active offer rules at the desk the morning you send.'
      ),
    ],
    bestPractices: [
      bullets([
        'Throttle frequency to avoid spam.',
        'Exclude recent complainers thoughtfully when selecting guests.',
        'Keep blackout dates in the message when inventory is tight.',
        'Measure replies and redeemed stays, not only sends.',
        'Align offer depth with Manage Rate floor rules.',
        'Use Customer Dashboard repeaters for white-glove gestures alongside mass retention.',
      ]),
    ],
    commonMistakes: [
      bullets([
        'Sending to the wrong segment.',
        'No FO briefing on active offers.',
        'Enabling WhatsApp in the UI when Communications is off.',
        'Over-discounting sold-out weekends.',
        'Re-sending identical copy too soon.',
        'Forgetting to select guests before send.',
      ]),
    ],
    troubleshooting: [
      table(
        ['Problem', 'Likely cause', 'What to do'],
        [
          [
            'Send failed',
            'Comms settings / connectivity',
            'Check Configuration customer_offer email/WhatsApp',
          ],
          [
            'No lapsed guests',
            'Window/history thin',
            'Widen strategy; improve profile capture first',
          ],
          [
            'Email ready = 0',
            'Emails never captured',
            'Capture emails at booking/add customer',
          ],
          [
            'Guests angry about honour',
            'Desk not briefed / blackout',
            'Stop campaign; brief FO; adjust future copy',
          ],
        ]
      ),
    ],
    faqs: [
      faq([
        {
          q: 'Is AI mandatory?',
          a: 'No — classic FO relationship still works; retention is an accelerator.',
        },
        {
          q: 'WhatsApp not sending?',
          a: 'Enable customer_offer WhatsApp in Communications and ensure guests have phone numbers.',
        },
        {
          q: 'Can I edit AI copy?',
          a: 'Yes — segment templates are editable; always preview.',
        },
        {
          q: 'Difference from Customer Detail offer?',
          a: 'Detail is one-to-one. AI Retention is segmented campaign outreach to many guests.',
        },
      ]),
    ],
  }),
];
